// Piezas pequeñas del portal que @iavmx/ui todavía no tiene: mensaje, interruptor, segmento, marco de acceso.
import * as React from "react";
import { AlertCircle, Check, Info } from "lucide-react";
import { cn, Wordmark } from "@iavmx/ui";

/** Mensaje en línea con hairline: info (neutro), error (rojo) u ok (verde). Nunca en silencio. */
export function Mensaje({
  tono = "info",
  titulo,
  children,
  className,
  role,
}: {
  tono?: "info" | "error" | "ok";
  titulo?: string;
  children: React.ReactNode;
  className?: string;
  role?: string;
}) {
  const Icono = tono === "error" ? AlertCircle : tono === "ok" ? Check : Info;
  return (
    <div
      role={role ?? (tono === "error" ? "alert" : "status")}
      className={cn(
        "flex gap-2.5 border p-3 font-ui text-sm leading-snug",
        tono === "error" && "border-rojo bg-rojo/5",
        tono === "ok" && "border-verde bg-verde/5",
        tono === "info" && "border-linea-fuerte",
        className,
      )}
    >
      <Icono
        className={cn(
          "mt-0.5 size-4 shrink-0",
          tono === "error" && "text-rojo",
          tono === "ok" && "text-verde",
          tono === "info" && "text-tinta-tenue",
        )}
        aria-hidden="true"
      />
      <div>
        {titulo && <p className="font-semibold">{titulo}</p>}
        <div className="text-tinta-2">{children}</div>
      </div>
    </div>
  );
}

/** Interruptor cuadrado, en tinta: sin colores de sistema. */
export function Interruptor({
  activo,
  onCambio,
  etiqueta,
  descripcion,
  deshabilitado,
}: {
  activo: boolean;
  onCambio: (v: boolean) => void;
  etiqueta: string;
  descripcion?: string;
  deshabilitado?: boolean;
}) {
  const id = React.useId();
  return (
    <div className="flex items-start gap-3 border-b border-linea py-3">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={activo}
        disabled={deshabilitado}
        onClick={() => onCambio(!activo)}
        className={cn(
          "relative mt-0.5 h-6 w-11 shrink-0 border border-tinta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-fondo disabled:opacity-40",
          activo ? "bg-tinta" : "bg-transparent",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-[3px] size-4 transition-[left]",
            activo ? "left-[23px] bg-tinta-inversa" : "left-[3px] bg-tinta",
          )}
        />
      </button>
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="block font-ui text-sm font-semibold">{etiqueta}</span>
        {descripcion && <span className="block font-ui text-sm text-tinta-tenue">{descripcion}</span>}
      </label>
    </div>
  );
}

/** Control segmentado: una opción activa en tinta. */
export function Segmento<T extends string>({
  opciones,
  valor,
  onCambio,
  etiqueta,
}: {
  opciones: { valor: T; nombre: string }[];
  valor: T;
  onCambio: (v: T) => void;
  etiqueta: string;
}) {
  return (
    <div role="radiogroup" aria-label={etiqueta} className="inline-flex border border-tinta">
      {opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={valor === o.valor}
          onClick={() => onCambio(o.valor)}
          className={cn(
            "min-h-10 px-3.5 font-ui text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            valor === o.valor ? "bg-tinta text-tinta-inversa" : "text-tinta-2 hover:bg-fondo-suave",
          )}
        >
          {o.nombre}
        </button>
      ))}
    </div>
  );
}

/** Chip de filtro con conteo. */
export function Chip({
  activo,
  onClick,
  children,
  n,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
  n?: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        "inline-flex min-h-11 items-center gap-2 border px-3 font-ui text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-9",
        activo ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea text-tinta-2 hover:bg-fondo-suave",
      )}
    >
      {children}
      {n !== undefined && <span className="font-bold">{n}</span>}
    </button>
  );
}

/** Marco de las pantallas sin sesión: barra con la marca y una columna centrada de 440 px. */
export function MarcoAcceso({
  children,
  derecha,
  ancho = "max-w-md",
}: {
  children: React.ReactNode;
  derecha?: React.ReactNode;
  ancho?: string;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-fondo text-tinta">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:bg-fondo focus:p-2 focus:ring-2 focus:ring-ring"
      >
        Ir al contenido
      </a>
      <header className="flex h-14 items-center gap-4 border-b border-linea px-5 sm:px-10">
        <Wordmark tamano="text-2xl" />
        <span className="hidden font-ui text-sm text-tinta-tenue sm:inline">
          Plataforma de herramientas de IA · Vanguardia
        </span>
        <div className="ml-auto font-ui text-sm text-tinta-2">{derecha}</div>
      </header>
      <main id="contenido" className="flex flex-1 items-start justify-center px-5 py-10 sm:py-16">
        <div className={cn("w-full", ancho)}>{children}</div>
      </main>
      <footer className="flex flex-wrap justify-between gap-2 px-5 pb-5 font-ui text-xs text-tinta-tenue sm:px-10">
        <span>iavmx · Vanguardia · Saltillo, Coahuila</span>
        <span>Aviso de privacidad interno</span>
      </footer>
    </div>
  );
}

export const NOMBRE_ORIGEN: Record<string, string> = {
  v360: "Vanguardia 360",
  semaforo: "Semáforo",
  comercial: "Comercial",
  coordinacion: "360 y Semáforo",
  portal: "Portal",
  datos: "Datos",
  facturas: "Facturas",
  personas: "Personas",
};
